import React from 'react';
import { Icon } from '../icons';
import { IconButton } from './Button';
import { useT } from '../../i18n';

const TONE_ICON = {
  info: Icon.Info,
  success: Icon.CircleCheck,
  warning: Icon.TriangleAlert,
  danger: Icon.CircleAlert,
  brand: Icon.Sparkles,
  neutral: Icon.Info,
};

// danger/warning are assertive; the rest are polite status messages.
const TONE_ROLE = {
  danger: 'alert',
  warning: 'alert',
};

/**
 * Banner — inline notification / alert strip.
 *
 * Props:
 *   tone: 'info' | 'success' | 'warning' | 'danger' | 'brand' | 'neutral'
 *   title: ReactNode                 — bold lead line (optional)
 *   icon: ReactNode                  — override the default tone icon (or null to hide)
 *   action: ReactNode                — trailing action(s), e.g. a Button
 *   onDismiss: () => void            — renders a close button when provided
 *   children: description body
 */
const Banner = ({
  tone = 'info',
  title,
  icon,
  action,
  onDismiss,
  className = '',
  children,
}) => {
  const t = useT();
  const ToneIcon = TONE_ICON[tone] || Icon.Info;
  const resolvedIcon = icon === undefined ? <ToneIcon size={18} /> : icon;

  return (
    <div
      className={`ps-banner ps-banner--${tone} ${className}`.trim()}
      role={TONE_ROLE[tone] || 'status'}
    >
      {resolvedIcon && <span className="ps-banner__icon">{resolvedIcon}</span>}
      <div className="ps-banner__content">
        {title && <p className="ps-banner__title">{title}</p>}
        {children && <div className="ps-banner__body">{children}</div>}
      </div>
      {action && <div className="ps-banner__action">{action}</div>}
      {onDismiss && (
        <IconButton
          aria-label={t('Dismiss')}
          variant="ghost"
          size="icon-sm"
          className="ps-banner__close"
          onClick={onDismiss}
        >
          <Icon.X />
        </IconButton>
      )}
    </div>
  );
};

export default Banner;
